import ListRowsSkeleton from "@/components/skeletons/ListRowsSkeleton";
import { useEffect, useState } from "react";
import { Plus, Users } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useSplitGroupStore } from "@/stores/useSplitGroupStore";
import { INPUT_CLASS } from "../types/friends.types";
import GroupCard from "./GroupCard";

export default function SplitGroupsTab() {
  const { groups, loading, loadGroups, createGroup } = useSplitGroupStore();
  const [newName, setNewName] = useState("");
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadGroups();
  }, []);

  async function handleCreate(e: { preventDefault(): void }) {
    e.preventDefault();
    const name = newName.trim();
    if (!name) return;
    setCreating(true);
    setError(null);
    try {
      await createGroup(name);
      setNewName("");
    } catch {
      setError("Failed to create group");
    } finally {
      setCreating(false);
    }
  }

  return (
    <>
      {error && <p className="text-sm text-red-500 mb-4">{error}</p>}

      <Card className="mb-6">
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-semibold flex items-center gap-2">
            <Users className="w-4 h-4" />
            Create new group
          </CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleCreate} className="flex gap-2">
            <input
              type="text"
              placeholder="Group name (e.g. Travel friends)"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              maxLength={100}
              className={`flex-1 min-w-0 ${INPUT_CLASS} py-2`}
            />
            <Button
              type="submit"
              disabled={!newName.trim()}
              loading={creating}
              size="sm"
              className="shrink-0"
            >
              {!creating && <Plus className="w-4 h-4 mr-1" />}
              {creating ? "Creating…" : "Create"}
            </Button>
          </form>
        </CardContent>
      </Card>

      {loading ? (
        <ListRowsSkeleton rows={2} variant="cards" />
      ) : groups.length === 0 ? (
        <p className="text-sm text-muted-foreground text-center py-10">
          No groups yet — create one above
        </p>
      ) : (
        <div className="flex flex-col gap-4">
          {groups.map((g) => (
            <GroupCard key={g.id} group={g} />
          ))}
        </div>
      )}
    </>
  );
}
